import { createAsyncThunk, Dispatch } from "@reduxjs/toolkit";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import {
  capitalize,
  cloneDeep,
  isFinite,
  isNumber,
  isString,
  set,
  startCase,
} from "lodash";
import { useLocation } from "react-router-dom";
import { RootState } from "../../store";
import urls from "../../urls";
import { MANY_ARGUMENTS } from "../constants/campaign";
import {
  FILE_UPLOAD_STATUS,
  LOADING_STATES,
  PRODUCTION_HOST,
  STAGING_HOST,
} from "../constants/common";
import {
  EMAIL_CONFIG_DATA_INIT,
  EMAIL_CONFIG_FIELDS,
  VALUE_META_TYPE,
} from "../constants/template";
import { setApiStatus } from "../slices/apiStatusSlice";
import { Account, UserListMap, USER_ACCOUNT_STATUS } from "../types/auth";
import { OperatorList, OperatorType } from "../types/campaign";
import { LoadingWithData } from "../types/common";
import { EVENT_TYPE } from "../types/connection";
import { PersonDestination, ProductPersonType } from "../types/person";
import { EmailConfigWithSubject } from "../types/template";
import { DESTINATION_TYPES } from "../types/unifiedMapping";
import hash from "object-hash";

export const convertCase = (data: string) => {
  return startCase(data);
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export const validatePassword = (password: string) => {
  return password.length >= 6;
};

export const validateEmail = (email: string) => {
  var pattern = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );

  return email !== "" && pattern.test(email);
};

export const formatLargeNumber = (num: number, digits: number) => {
  const lookup = [
    { value: 1e18, symbol: "Quinitillion" },
    { value: 1e15, symbol: "Quadillion" },
    { value: 1e12, symbol: "Trillion" },
    { value: 1e9, symbol: "Billion" },
    { value: 1e6, symbol: "Million" },
    { value: 1, symbol: "" },
  ];
  //To check if decimal place <= 0.99
  const floatLessThanOne = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup.find((item) => num >= item.value);
  return item
    ? `${(num / item.value).toFixed(digits).replace(floatLessThanOne, "$1")} ${
        item.symbol
      }`
    : "0";
};

export function isLoading(status?: LOADING_STATES) {
  return status === LOADING_STATES.LOADING;
}

export function isSuccess(status?: LOADING_STATES) {
  return status === LOADING_STATES.SUCCESS;
}

export function isInit(status?: LOADING_STATES) {
  return status === LOADING_STATES.INIT;
}

export function isFailed(status?: LOADING_STATES) {
  return status === LOADING_STATES.FAILED;
}

export function isFinished(status?: LOADING_STATES) {
  return status === LOADING_STATES.FAILED || status === LOADING_STATES.SUCCESS;
}

export function isRejected(checkString: string) {
  return checkString === "rejected";
}

export function isFulfilled(checkString: string) {
  return checkString === "fulfilled";
}

export const formatNumber = (number: number): string => {
  return isFinite(number) ? number.toLocaleString("en-US") : "-";
};

export const convertToString = (
  value: string | number | null,
  fallback?: string
) => {
  if (isString(value)) {
    return formatNumber(+value);
  } else if (isNumber(value)) {
    return formatNumber(value);
  } else {
    return fallback ?? "";
  }
};

// Returns a synthetic event when passed an event
// Using this now for creating an input onChange event for email restriction component to efficiently use formik functionality
export const createSyntheticEvent = <T extends Element, E extends Event>(
  event: E
): React.SyntheticEvent<T, E> => {
  let isDefaultPrevented = false;
  let isPropagationStopped = false;
  const preventDefault = () => {
    isDefaultPrevented = true;
    event.preventDefault();
  };
  const stopPropagation = () => {
    isPropagationStopped = true;
    event.stopPropagation();
  };
  return {
    nativeEvent: event,
    currentTarget: event.currentTarget as EventTarget & T,
    target: event.target as EventTarget & T,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    defaultPrevented: event.defaultPrevented,
    eventPhase: event.eventPhase,
    isTrusted: event.isTrusted,
    preventDefault,
    isDefaultPrevented: () => isDefaultPrevented,
    stopPropagation,
    isPropagationStopped: () => isPropagationStopped,
    persist: () => {},
    timeStamp: event.timeStamp,
    type: event.type,
  };
};

export const customOnChangeHandler = (
  field: string,
  newValue: any,
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
) => {
  const target = document.createElement("input");
  target.value = newValue;
  target.name = field;
  const event = new Event("change", { bubbles: true });
  Object.defineProperty(event, "target", { writable: false, value: target });
  const syntheticEvent = createSyntheticEvent(event) as React.ChangeEvent<
    typeof target
  >;
  if (onChange) {
    onChange(syntheticEvent);
  }
  target.remove();
};

type GroupTypes = "outer" | "filter" | "property";

// For dynamic list side bar use
export const createGroupId = (
  parentId: string,
  groupType: GroupTypes,
  index: number
) => {
  return `${parentId}-${groupType}-group-${index}`;
};

export const percentageOf = (
  data: number,
  total: number,
  appendSymbol = false
) => {
  const percentage = total && data ? (data * 100) / total : 0;
  const percentageFloat = +parseFloat(`${percentage}`).toFixed(2);
  return appendSymbol ? `${percentageFloat}%` : percentageFloat;
};

export const onEnterKeySubmit = (
  e: React.KeyboardEvent<HTMLInputElement>,
  callback: (param?: string) => void
) => {
  if (e.key === "Enter") {
    callback.apply(null, []);
  }
};

export const keepClickEventRestricted = (
  e: React.MouseEvent<
    HTMLButtonElement | HTMLInputElement | HTMLDivElement,
    MouseEvent
  >,
  callback?: (param?: string) => void
) => {
  e.preventDefault();
  e.stopPropagation();
  callback?.apply(null, []);
};

export const textLimitter = (text: string, length: number) => {
  if (text.length > length) return `${text.slice(0, length)}...`;
  return text;
};

export function getShortTimezoneName() {
  return new Date()
    .toLocaleDateString("en", { day: "2-digit", timeZoneName: "short" })
    .slice(4);
}

export function initializeLoadingData(
  initData: any
): LoadingWithData<typeof initData> {
  return {
    data: initData,
    loading: LOADING_STATES.INIT,
  };
}

export function downloadFile(file: Blob, filename: string) {
  if (file) {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  }
}

export function isProduction() {
  return document.location.host === PRODUCTION_HOST;
}
export function isStaging() {
  return document.location.host === STAGING_HOST;
}
export function isUnsubLandingPage() {
  return document.location.pathname === urls.unsubscribed;
}

// File upload status checker
export function isUploadSuccess(status: FILE_UPLOAD_STATUS | undefined) {
  return status === FILE_UPLOAD_STATUS.COMPLETED;
}

export function isUploadError(status: FILE_UPLOAD_STATUS | undefined) {
  return status === FILE_UPLOAD_STATUS.ERROR;
}

export function isUploadProcessing(status: FILE_UPLOAD_STATUS | undefined) {
  return status === FILE_UPLOAD_STATUS.PROCESSING;
}

export function generateRandomString(length: number, prefixText?: string) {
  const ALPHABETS =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let randomString = "";

  for (let i = 0; i < length; i++) {
    randomString += ALPHABETS[Math.floor(Math.random() * ALPHABETS.length)];
  }

  return prefixText ? prefixText + randomString : randomString;
}

export function openSalesforceInstance({
  url,
  crmObject,
  salesforceId,
}: {
  url: string;
  crmObject: string;
  salesforceId: string;
}) {
  window.open(`${url}/lightning/r/${crmObject}/${salesforceId}/view`);
}

export function formatLastSaved({
  updatedAt = "",
  includeSeconds = false,
  addSuffix = true,
  locale,
}: {
  updatedAt: string;
  includeSeconds?: boolean;
  addSuffix?: boolean;
  locale?: Locale;
}) {
  return updatedAt
    ? "Last saved " +
        formatDistanceToNow(new Date(updatedAt), {
          addSuffix,
          includeSeconds,
          locale,
        })
    : "";
}

export function validateUrl(url: string) {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "https:" || newUrl.protocol === "http:";
  } catch (e) {
    return false;
  }
}

export function isBlank(value: any) {
  return value === undefined || value === "" || value === null;
}

export function createUserMapping(usersList: Account[]) {
  let keyedUsers: UserListMap = {};
  keyedUsers = usersList.reduce((map, user) => {
    map[user.id] = user;
    return map;
  }, keyedUsers);
  return keyedUsers;
}

export function findUserFromMapping(id: string, usersListMap: UserListMap) {
  return usersListMap[id]?.name ?? "";
}

export function getUserStatusFromMapping(
  id: string,
  usersListMap: UserListMap
) {
  return usersListMap[id]?.status ?? USER_ACCOUNT_STATUS.PENDING;
}

export function formatDataForDisplay(
  key: string,
  value: any,
  dataType: DESTINATION_TYPES
) {
  return {
    key: key,
    details: {
      type: dataType,
      display: capitalize(key.replaceAll("_", " ")),
      value: value,
      read_only: true,
      required: false,
      length: null,
      name: "",
      custom: false,
      hidden: false,
    },
  };
}

export function addSuffixForPlural(text: string, count: number | undefined) {
  return `${text}${count && count > 1 ? "s" : ""}`;
}

export function checkIsAccountInactive(accountStatus: string) {
  return accountStatus === USER_ACCOUNT_STATUS.INACTIVE;
}

export function isTrackEvent(segmentEventName: string) {
  return [EVENT_TYPE.GROUP, EVENT_TYPE.IDENTIFY].includes(segmentEventName);
}

export function getEventTypeAndName(eventName: string) {
  return {
    event_type: isTrackEvent(eventName) ? eventName : EVENT_TYPE.TRACK,
    event_name: isTrackEvent(eventName) ? undefined : eventName,
  };
}

export function sortDetails(
  first: [string, PersonDestination],
  second: [string, PersonDestination]
) {
  const firstReadonly = !!first[1].read_only;
  const secondReadonly = !!second[1].read_only;
  const firstDisplayName = first[1].display ?? "z";
  const secondDisplayName = second[1].display ?? "z";
  if (firstReadonly === secondReadonly) {
    return firstReadonly ? 0 : firstDisplayName > secondDisplayName ? 1 : -1;
  } else {
    return firstReadonly ? -1 : 1;
  }
}

export function sortAlphabetically(first: string, second: string) {
  var textA = first.toUpperCase();
  var textB = second.toUpperCase();
  return textA < textB ? -1 : textA > textB ? 1 : 0;
}

export function convertToProductPersonType(
  filteredEntries: [string, PersonDestination][]
) {
  const newEntries: ProductPersonType[] = [];
  filteredEntries.forEach((value) => {
    newEntries.push({ key: value[0], details: value[1] });
  });
  return newEntries;
}

export function isArgumentMany(argument?: string) {
  return argument === MANY_ARGUMENTS;
}

function checkAndSetErrors(
  value: string,
  key: string,
  message: string,
  errors: EmailConfigWithSubject,
  email = false
): {
  validity: boolean;
  newErrors: EmailConfigWithSubject;
} {
  const errorMsg = (value && !email) || validateEmail(value) ? "" : message;
  const newErrors = cloneDeep(errors);
  set(newErrors, key, errorMsg);
  return {
    validity: !errorMsg,
    newErrors,
  };
}

export function validateEmailConfigInputs(inputData: EmailConfigWithSubject) {
  const errors = EMAIL_CONFIG_DATA_INIT;
  const subjectError = checkAndSetErrors(
    inputData.subject,
    EMAIL_CONFIG_FIELDS.SUBJECT,
    "Email subject should not be empty",
    errors
  );
  const nameError = checkAndSetErrors(
    inputData.from_email.name,
    EMAIL_CONFIG_FIELDS.FROM_EMAIL_NAME,
    "Sender name should not be empty",
    subjectError.newErrors
  );

  let sentFromError = {
    validity: nameError.validity,
    newErrors: nameError.newErrors,
  };
  if (inputData.sender_meta?.from_email === VALUE_META_TYPE.TEXT) {
    sentFromError = checkAndSetErrors(
      inputData.from_email.email,
      EMAIL_CONFIG_FIELDS.FROM_EMAIL,
      "Please enter a valid email address",
      nameError.newErrors,
      true
    );
  } else {
    sentFromError = checkAndSetErrors(
      inputData.from_email.email,
      EMAIL_CONFIG_FIELDS.FROM_EMAIL,
      "Send from should not be empty",
      nameError.newErrors
    );
  }

  let replyToError = {
    validity: sentFromError.validity,
    newErrors: sentFromError.newErrors,
  };
  if (inputData.sender_meta?.reply_to === VALUE_META_TYPE.TEXT) {
    replyToError = checkAndSetErrors(
      inputData.reply_to,
      EMAIL_CONFIG_FIELDS.REPLY_TO,
      "Please enter a valid email address",
      sentFromError.newErrors,
      true
    );
  } else {
    replyToError = checkAndSetErrors(
      inputData.reply_to,
      EMAIL_CONFIG_FIELDS.REPLY_TO,
      "Reply to should not be empty",
      sentFromError.newErrors
    );
  }

  return {
    validity: [subjectError, nameError, sentFromError, replyToError].every(
      (item) => item.validity
    ),
    errors: replyToError.newErrors,
  };
}

type AsyncThunkConfig = {
  getState: () => unknown;
  dispatch: Dispatch;
};

function apiCallValidation(actionName: string, props: any, state: RootState) {
  const {
    apiStatus: { apiStatus },
  } = state;
  if (actionName in apiStatus) {
    const propsHash = hash(props ?? {});
    if (propsHash in apiStatus[actionName]) {
      // Expiration check
      if ((apiStatus[actionName][propsHash].expiry as number) < Date.now())
        return true;
      // whether already called api check
      if (!isInit(apiStatus[actionName][propsHash].state)) return false;
    }
  }
  return true;
}

export function createAsyncThunkWrapper<U, T = void>({
  actionName,
  dispatchFn,
  apiCallRestrictionFn,
  isCachable = false,
}: {
  actionName: string;
  dispatchFn: (args: T, thunkApi: AsyncThunkConfig) => Promise<U>;
  apiCallRestrictionFn?: (args: T, getState: () => unknown) => boolean;
  isCachable?: boolean;
}) {
  return createAsyncThunk(
    actionName,
    async (args: T, { getState, dispatch }) => {
      // adding the api status to pending state/already called state
      isCachable &&
        dispatch(
          setApiStatus({
            actionName: actionName,
            props: args,
          })
        );

      return await dispatchFn(args, { getState, dispatch });
    },
    {
      condition: (args: T, { getState }) => {
        //custom condition to skip api call
        const customCondition = apiCallRestrictionFn
          ? apiCallRestrictionFn(args, getState)
          : true;
        //caching condition to skip api call
        const cacheValidation = isCachable
          ? apiCallValidation(actionName, args, getState() as RootState)
          : true;

        return (
          (args as unknown as { isForced: boolean })?.isForced ||
          (customCondition && cacheValidation)
        );
        //TODO: Hotfix for Forms V1. Remove unknown type casting and add flexibility to call dispatch forcibly even it's cached.
      },
    }
  );
}

export function getOperatorDetails(
  operator: string | null,
  operators: OperatorList | null,
  type?: OperatorType
) {
  return operator && operators && type
    ? operators[type][operator] ?? null
    : null;
}
